/**
 * Onboarding State Management
 *
 * Tracks user progress through the onboarding flow and post-onboarding checklist.
 * State is persisted to localStorage for cross-session tracking.
 */

const STORAGE_KEY = 'budget_onboarding_state';

export type OnboardingState = {
  // Initial onboarding flow
  hasSeenWelcome: boolean;
  hasCompletedWizard: boolean;
  wizardSkipped: boolean;

  // Post-onboarding checklist items
  hasUploadedFirstFile: boolean;
  hasReviewedCategories: boolean;
  hasCreatedFirstRule: boolean;
  hasSetupBudget: boolean;
  hasExploredRecurring: boolean;

  // UI state
  checklistDismissed: boolean;
  lastUpdated: string;
};

const DEFAULT_STATE: OnboardingState = {
  hasSeenWelcome: false,
  hasCompletedWizard: false,
  wizardSkipped: false,
  hasUploadedFirstFile: false,
  hasReviewedCategories: false,
  hasCreatedFirstRule: false,
  hasSetupBudget: false,
  hasExploredRecurring: false,
  checklistDismissed: false,
  lastUpdated: new Date().toISOString(),
};

/**
 * Load onboarding state from localStorage
 */
export function loadOnboardingState(): OnboardingState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { ...DEFAULT_STATE };
    }

    const parsed = JSON.parse(stored);
    return { ...DEFAULT_STATE, ...parsed };
  } catch (error) {
    console.error('Failed to load onboarding state:', error);
    return { ...DEFAULT_STATE };
  }
}

/**
 * Save onboarding state to localStorage
 */
export function saveOnboardingState(state: OnboardingState): void {
  try {
    const updated = {
      ...state,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save onboarding state:', error);
  }
}

/**
 * Check if this is a first-time user (hasn't seen welcome screen)
 */
export function isFirstTimeUser(): boolean {
  const state = loadOnboardingState();
  return !state.hasSeenWelcome;
}

/**
 * Check if onboarding wizard should be shown
 */
export function shouldShowWizard(): boolean {
  const state = loadOnboardingState();
  return state.hasSeenWelcome && !state.hasCompletedWizard && !state.wizardSkipped;
}

/**
 * Check if onboarding checklist should be shown
 */
export function shouldShowChecklist(): boolean {
  const state = loadOnboardingState();

  // Don't show if explicitly dismissed
  if (state.checklistDismissed) {
    return false;
  }

  // Don't show if user hasn't completed initial onboarding
  if (!state.hasSeenWelcome) {
    return false;
  }

  // Don't show if all tasks are complete
  const allTasksComplete =
    state.hasUploadedFirstFile &&
    state.hasReviewedCategories &&
    state.hasCreatedFirstRule &&
    state.hasSetupBudget &&
    state.hasExploredRecurring;

  return !allTasksComplete;
}

/**
 * Mark welcome screen as seen
 */
export function markWelcomeSeen(): void {
  const state = loadOnboardingState();
  saveOnboardingState({ ...state, hasSeenWelcome: true });
}

/**
 * Mark wizard as completed
 */
export function markWizardCompleted(): void {
  const state = loadOnboardingState();
  saveOnboardingState({ ...state, hasCompletedWizard: true });
}

/**
 * Mark wizard as skipped
 */
export function markWizardSkipped(): void {
  const state = loadOnboardingState();
  saveOnboardingState({ ...state, wizardSkipped: true });
}

/**
 * Mark a checklist item as complete
 */
export function markChecklistItem(
  item: 'upload' | 'categories' | 'rule' | 'budget' | 'recurring',
  completed: boolean = true
): void {
  const state = loadOnboardingState();

  const updates: Partial<OnboardingState> = {};

  switch (item) {
    case 'upload':
      updates.hasUploadedFirstFile = completed;
      break;
    case 'categories':
      updates.hasReviewedCategories = completed;
      break;
    case 'rule':
      updates.hasCreatedFirstRule = completed;
      break;
    case 'budget':
      updates.hasSetupBudget = completed;
      break;
    case 'recurring':
      updates.hasExploredRecurring = completed;
      break;
  }

  saveOnboardingState({ ...state, ...updates });
}

/**
 * Dismiss the checklist permanently
 */
export function dismissChecklist(): void {
  const state = loadOnboardingState();
  saveOnboardingState({ ...state, checklistDismissed: true });
}

/**
 * Reset onboarding state (for "Restart Onboarding" feature)
 */
export function resetOnboarding(): void {
  saveOnboardingState({ ...DEFAULT_STATE });
}

/**
 * Get checklist completion percentage (0-100)
 */
export function getChecklistProgress(): number {
  const state = loadOnboardingState();

  const tasks = [
    state.hasUploadedFirstFile,
    state.hasReviewedCategories,
    state.hasCreatedFirstRule,
    state.hasSetupBudget,
    state.hasExploredRecurring,
  ];

  const completedCount = tasks.filter(Boolean).length;
  return Math.round((completedCount / tasks.length) * 100);
}

/**
 * Get detailed checklist status for UI rendering
 */
export function getChecklistStatus() {
  const state = loadOnboardingState();

  return {
    items: [
      {
        id: 'upload',
        title: 'Upload your first CSV file',
        description: 'Import transactions from your bank',
        completed: state.hasUploadedFirstFile,
        link: '#/settings',
      },
      {
        id: 'categories',
        title: 'Review auto-categorized transactions',
        description: 'Check how your transactions were categorized',
        completed: state.hasReviewedCategories,
        link: '#/',
      },
      {
        id: 'rule',
        title: 'Create your first rule',
        description: 'Automatically categorize recurring merchants',
        completed: state.hasCreatedFirstRule,
        link: '#/settings',
      },
      {
        id: 'budget',
        title: 'Set up a budget',
        description: 'Track spending limits for categories',
        completed: state.hasSetupBudget,
        link: '#/budgets',
      },
      {
        id: 'recurring',
        title: 'Explore recurring transactions',
        description: 'Find subscriptions and recurring bills',
        completed: state.hasExploredRecurring,
        link: '#/recurring',
      },
    ],
    progress: getChecklistProgress(),
    allComplete: getChecklistProgress() === 100,
  };
}
